import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { Text, Tooltip } from '@zillow/constellation-10';
import type { ComponentProps } from 'react';

interface TruncatedTextWithTooltipProps {
  text: string;
  textStyle?: ComponentProps<typeof Text>['textStyle'];
  color?: ComponentProps<typeof Text>['color'];
  css?: ComponentProps<typeof Text>['css'];
  asChild?: boolean;
  children?: React.ReactNode;
  onTruncationChange?: (isTruncated: boolean) => void;
}

/**
 * Component that displays text with ellipsis truncation and shows a tooltip
 * when the text is actually truncated. Only renders the tooltip when needed
 * for optimal performance.
 */
export const TruncatedTextWithTooltip = ({
  text,
  textStyle,
  color,
  css,
  asChild,
  children,
  onTruncationChange,
}: TruncatedTextWithTooltipProps) => {
  const textRef = useRef<HTMLElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const checkTruncation = useCallback(() => {
    if (textRef.current) {
      const isTruncated = textRef.current.scrollWidth > textRef.current.clientWidth;
      setShowTooltip(isTruncated);
      onTruncationChange?.(isTruncated);
    }
  }, [onTruncationChange]);

  useLayoutEffect(() => {
    checkTruncation();

    // Recheck on window resize for responsive behavior
    const handleResize = () => {
      checkTruncation();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [checkTruncation, text]); // Depend on text to recheck when content changes

  const textElement = (
    <Text
      ref={textRef}
      textStyle={textStyle}
      color={color}
      css={{
        cursor: showTooltip ? 'help' : 'default',
        outline: 'none', // We'll rely on browser focus styles
        ...css,
      }}
      asChild={asChild}
      tabIndex={showTooltip ? 0 : undefined}
    >
      {children || text}
    </Text>
  );

  // Only render tooltip when text is actually truncated
  if (!showTooltip) {
    return textElement;
  }

  return <Tooltip content={text} placement="bottom-start" trigger={textElement} />;
};

export default TruncatedTextWithTooltip;
